import {
  QuestionBank,
  QuestionBankCreateDto,
  QuestionMatchingOptions,
} from '../../../../core/interfaces/question';
import {
  DifficultyLevel,
  QuestionEditorValue,
  QuestionType,
} from './question-editor.component';

/**
 * Traduit la valeur produite par l'éditeur en charge utile de création.
 *
 * Deux responsabilités laissées au serveur, volontairement absentes d'ici :
 * - `displayOrder` — il n'a de sens que pour ordonner les matières ; l'ordre
 *   des réponses est dérivé de leur position dans la liste envoyée ;
 * - `professorId` / `createdBy` — dérivés du jeton.
 *
 * Les réglages de correction (`matchingOptions`) sont en revanche transmis :
 * le serveur les persiste en `jsonb` depuis la migration
 * `AddQuestionMatchingOptions`.
 */
export function toCreateDto(
  value: QuestionEditorValue,
  themeId: number,
): QuestionBankCreateDto {
  return {
    title: value.title,
    description: value.description || undefined,
    questionType: value.questionType,
    difficultyLevel: value.difficultyLevel,
    points: value.points,
    timeLimitSeconds: value.timeLimitSeconds ?? undefined,
    themeId,
    explanation: value.explanation || undefined,
    hints: value.hints || undefined,
    tags: value.tags,
    matchingOptions: toMatchingOptions(value),
    // `null` et non `undefined` : c'est ce qui distingue « je retire
    // l'illustration » de « je n'y touche pas ».
    descriptionImageKey: value.descriptionImageKey ?? null,
    answerBanks: toAnswerBanks(value),
  };
}

/**
 * Réglages de correction propres au type.
 *
 * `undefined` pour les questions à choix : elles se corrigent par comparaison
 * d'identifiants, aucun réglage n'a de sens.
 */
function toMatchingOptions(
  value: QuestionEditorValue,
): QuestionMatchingOptions | undefined {
  const correction = toCorrectionSettings(value);
  const referential = pruneReferential(value.referential);

  // Le `jsonb` porte les deux : les réglages de correction et les descripteurs
  // du référentiel. Il est réécrit en entier à chaque enregistrement — omettre
  // les seconds les effaçait. Une question importée est un choix simple ou
  // multiple, donc précisément le cas qui renvoyait `undefined`.
  if (!correction && !referential) return undefined;

  // Étaler `undefined` ne produit rien : la garde ci-dessus assure qu'au moins
  // l'un des deux apporte quelque chose.
  return { ...correction, ...referential } as QuestionMatchingOptions;
}

/**
 * Réglages de correction, ou `undefined` pour les questions à choix : elles se
 * corrigent par comparaison d'identifiants, aucun réglage n'a de sens.
 */
function toCorrectionSettings(
  value: QuestionEditorValue,
): QuestionMatchingOptions | undefined {
  if (value.questionType === 'short_text') {
    const options = value.textAnswers;
    return {
      caseSensitive: options?.caseSensitive ?? false,
      trimWhitespace: options?.trimWhitespace ?? true,
      normalizeArabic: options?.normalizeArabic ?? true,
      tolerance: 0,
    };
  }

  if (value.questionType === 'numeric') {
    return {
      caseSensitive: false,
      trimWhitespace: true,
      normalizeArabic: false,
      tolerance: value.numericAnswer?.tolerance ?? 0,
    };
  }

  return undefined;
}

/**
 * Les descripteurs réellement renseignés, ou `undefined` s'il n'y en a aucun.
 *
 * <p>On ne renvoie pas d'objet vide : cela transformerait « rien à dire » en
 * « quatre champs vides », et écraserait des valeurs que le serveur détient.</p>
 */
function pruneReferential(
  referential: QuestionEditorValue['referential'],
): QuestionEditorValue['referential'] | undefined {
  if (!referential) return undefined;

  const kept = Object.fromEntries(
    Object.entries(referential).filter(([, v]) => typeof v === 'string' && v.length > 0),
  );

  return Object.keys(kept).length > 0 ? kept : undefined;
}

/**
 * L'ordre des réponses n'est pas transmis : le serveur le déduit de leur
 * position dans cette liste. Le champ `displayOrder` du DTO est conservé pour
 * la lecture, mais reste à `0` à l'envoi.
 */
function toAnswerBanks(
  value: QuestionEditorValue,
): QuestionBankCreateDto['answerBanks'] {
  switch (value.questionType) {
    case 'single_choice':
    case 'multiple_choice':
    case 'true_false':
      return (value.choices ?? []).map((choice) => ({
        content: choice.content,
        isCorrect: choice.isCorrect,
        // Le barème par réponse n'est pas encore exploité par la correction
        // (tout-ou-nothing) : on reporte les points de la question.
        points: choice.isCorrect ? value.points : 0,
      }));

    case 'short_text':
      // Chaque réponse acceptée devient une réponse correcte ; les règles de
      // comparaison voyagent séparément dans `matchingOptions`.
      return (value.textAnswers?.answers ?? []).map((answer) => ({
        content: answer,
        isCorrect: true,
        points: value.points,
      }));

    case 'numeric': {
      const expected = value.numericAnswer?.value;
      return expected === null || expected === undefined
        ? []
        : [
            {
              content: String(expected),
              isCorrect: true,
              points: value.points,
            },
          ];
    }

    default:
      return [];
  }
}

/**
 * Traduit une question chargée depuis l'API en valeur d'éditeur, pour
 * l'édition.
 *
 * L'inverse de {@link toCreateDto}. Les réponses stockées sont dépliées vers la
 * forme attendue par chaque éditeur : options pour les choix, liste acceptée
 * pour le texte libre, valeur + tolérance pour le numérique. Les réglages de
 * correction (casse, normalisation, tolérance) sont lus depuis
 * `matchingOptions` quand le serveur les a renvoyés.
 */
export function toEditorValue(q: QuestionBank): QuestionEditorValue {
  const type = q.questionType as QuestionType;

  const value: QuestionEditorValue = {
    id: q.id,
    questionType: type,
    title: q.title ?? '',
    description: q.description ?? '',
    points: q.points ?? 1,
    difficultyLevel: (q.difficultyLevel as DifficultyLevel) ?? 'easy',
    timeLimitSeconds: q.timeLimitSeconds ?? null,
    explanation: q.explanation ?? '',
    hints: q.hints ?? '',
    tags: q.tags ?? [],
    descriptionImageKey: q.descriptionImageKey ?? null,
    descriptionImageUrl: q.descriptionImageUrl ?? null,
  };

  const answers = [...(q.answerBanks ?? [])].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
  );
  const options = q.matchingOptions;

  // Les descripteurs traversent le formulaire même si aucun champ ne les
  // affiche : sans cette reprise, le `jsonb` repartait sans eux et la question
  // importée perdait son rattachement au référentiel dès la première retouche.
  if (options?.level || options?.skill || options?.prerequisites || options?.targeted) {
    value.referential = {
      level: options.level ?? null,
      skill: options.skill ?? null,
      prerequisites: options.prerequisites ?? null,
      targeted: options.targeted ?? null,
    };
  }

  switch (type) {
    case 'single_choice':
    case 'multiple_choice':
    case 'true_false':
      value.choices = answers.map((a) => ({
        content: a.content ?? '',
        isCorrect: a.isCorrect,
      }));
      break;

    case 'short_text':
      value.textAnswers = {
        answers: answers.map((a) => a.content ?? '').filter((c) => c.length > 0),
        caseSensitive: options?.caseSensitive ?? false,
        trimWhitespace: options?.trimWhitespace ?? true,
        normalizeArabic: options?.normalizeArabic ?? true,
      };
      break;

    case 'numeric': {
      const first = answers[0]?.content;
      const parsed = first !== undefined ? Number(first) : NaN;
      value.numericAnswer = {
        value: Number.isFinite(parsed) ? parsed : null,
        tolerance: options?.tolerance ?? 0,
      };
      break;
    }
  }

  return value;
}
